import React, { useState, useEffect } from 'react';
import ColaboradorLayout from '../../components/ColaboradorLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { preferencesAPI } from '../../services/api';
import { Settings, Loader2, Save } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format, startOfWeek, addDays } from 'date-fns';

const DAYS = [
  { value: 0, label: 'Lunes' },
  { value: 1, label: 'Martes' },
  { value: 2, label: 'Miércoles' },
  { value: 3, label: 'Jueves' },
  { value: 4, label: 'Viernes' },
];

const PreferencesPage = () => {
  const [preferences, setPreferences] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Calculate next week's start date
  const today = new Date();
  const nextWeekStart = startOfWeek(addDays(today, 7), { weekStartsOn: 1 });
  const weekStartDate = format(nextWeekStart, 'yyyy-MM-dd');

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await preferencesAPI.getAll(weekStartDate);
      setPreferences(response.data);
      if (response.data.length > 0) {
        setSelectedDays(response.data[0].preferred_days);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDayToggle = (day) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter(d => d !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await preferencesAPI.create({
        preferred_days: selectedDays,
        week_start_date: weekStartDate
      });
      toast.success('Preferencias guardadas exitosamente');
      loadPreferences();
    } catch (error) {
      toast.error('Error al guardar preferencias');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ColaboradorLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Mis Preferencias
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Indica tus días preferidos de asistencia presencial
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#002FA7]" />
                  Preferencias para la semana
                </CardTitle>
                <CardDescription>
                  Semana del {format(nextWeekStart, 'dd/MM/yyyy')} al {format(addDays(nextWeekStart, 4), 'dd/MM/yyyy')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600">
                  Selecciona los días que preferirías asistir a la oficina. 
                  Recuerda que las preferencias no garantizan la asignación.
                </p>

                <div className="space-y-3">
                  {DAYS.map((day) => (
                    <div 
                      key={day.value}
                      className={`flex items-center gap-3 p-3 rounded-sm border transition-colors cursor-pointer ${
                        selectedDays.includes(day.value) 
                          ? 'bg-blue-50 border-blue-300' 
                          : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                      onClick={() => handleDayToggle(day.value)}
                    >
                      <Checkbox
                        checked={selectedDays.includes(day.value)}
                        onCheckedChange={() => handleDayToggle(day.value)}
                        data-testid={`pref-day-${day.value}`}
                      />
                      <span className="font-medium">{day.label}</span>
                      <span className="text-sm text-slate-500 ml-auto">
                        {format(addDays(nextWeekStart, day.value), 'dd/MM')}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="pt-4">
                  <Button
                    onClick={handleSave}
                    className="w-full bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
                    disabled={saving}
                    data-testid="save-preferences-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Preferencias
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Información Importante</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-sm">
                  <p className="text-sm text-yellow-800">
                    <strong>Nota:</strong> Las preferencias son solo indicativas. El sistema utiliza un algoritmo 
                    de equidad que considera múltiples factores para realizar las asignaciones.
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium text-sm">El sistema considera:</h4>
                  <ul className="text-sm text-slate-600 space-y-1 ml-4">
                    <li>• Tu historial de asignaciones</li>
                    <li>• Capacidad diaria de la sede</li>
                    <li>• Prioridad de tu rol</li>
                    <li>• Necesidad de coincidencia con tu equipo</li>
                    <li>• Distribución equitativa entre todos</li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Días seleccionados:</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedDays.length === 0 ? (
                      <span className="text-slate-400 text-sm">Ninguno seleccionado</span>
                    ) : (
                      selectedDays.sort().map((d) => (
                        <span key={d} className="badge-info">
                          {DAYS.find(day => day.value === d)?.label}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </ColaboradorLayout>
  );
};

export default PreferencesPage;
